"use client";
// De dónde lee y escribe el panel. Con servidor: la API (PostgreSQL). En la vista previa estática: el navegador,
// partiendo de una copia de los datos de prueba, con las mismas reglas que el servidor.
import { aBase, serializar, type Fila } from "./convertir";
import { accionLocal, altaAgenciaLocal, prepararTicketLocal, quienEsLocal, sinSecretos } from "./navegador";
import { camposLeibles, entradaPermitida, recursoPor, type Recurso, type Rol } from "./recursos";
import { altaProveedorLocal, asegurarDemoReservas } from "./reservas-local";

/** Solo los campos que este rol puede leer, como en el servidor: sin sueldos fuera de Dirección y RH, sin secretos. */
export function visibleDe(r: Recurso, rol: Rol, f: Fila): Fila {
  const permitidos = new Set(["id", ...camposLeibles(r, rol).map((c) => c.nombre)]);
  return Object.fromEntries(Object.entries(f).filter(([k]) => permitidos.has(k))) as Fila;
}

/** El módulo debe existir y tener el rol entre sus roles (igual que recursoPermitido del servidor). */
export function moduloPara(clave: string, rol: Rol): Recurso {
  const r = recursoPor(clave);
  if (!r) throw new Error("Módulo no encontrado");
  if (!r.roles.includes(rol)) throw new Error("Tu rol no tiene este módulo");
  return r;
}

export type Proveedor = {
  modo: "servidor" | "navegador";
  listar: (clave: string) => Promise<Fila[]>;
  crear: (clave: string, datos: Record<string, unknown>) => Promise<Fila>;
  editar: (clave: string, id: string, datos: Record<string, unknown>) => Promise<Fila>;
  borrar: (clave: string, id: string) => Promise<void>;
  accion: (clave: string, id: string, nombre: string, datos: Record<string, string>) => Promise<{ mensaje: string }>;
  reiniciar?: () => Promise<void>;
};

export function proveedorServidor(rol: () => Rol): Proveedor {
  void rol; // La sesión de servidor establece identidad; el rol visual no se envía como autoridad.
  const pedir = async (url: string, init?: RequestInit) => {
    const r = await fetch(url, { ...init, headers: { "content-type": "application/json" } });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.mensaje ?? "Error");
    return d;
  };
  return {
    modo: "servidor",
    listar: (c) => pedir(`/api/panel/${c}`),
    crear: (c, d) => pedir(`/api/panel/${c}`, { method: "POST", body: JSON.stringify(d) }),
    editar: (c, id, d) => pedir(`/api/panel/${c}/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
    borrar: async (c, id) => { await pedir(`/api/panel/${c}/${id}`, { method: "DELETE" }); },
    accion: (c, id, n, d) => pedir(`/api/panel/${c}/${id}/${n}`, { method: "POST", body: JSON.stringify(d) }),
  };
}

const LLAVE = "panel-demo-v2";
export type Tablas = Record<string, Fila[]>;

/** Almacén de la vista previa: una sola copia de los datos de prueba compartida por panel, app de calle, agencias y boleto. */
export type Almacen = { t: () => Tablas; tabla: (c: string) => Fila[]; guardar: () => void; reiniciar: () => Promise<void>; nuevoId: () => string };
let almacenActual: Promise<Almacen> | null = null;
export function almacenNavegador(urlDatos: string): Promise<Almacen> {
  return (almacenActual ??= (async () => {
    const original = async (): Promise<Tablas> => (await (await fetch(urlDatos)).json()).tablas;
    let t: Tablas;
    try { t = JSON.parse(localStorage.getItem(LLAVE) ?? "null") ?? (await original()); } catch { t = await original(); }
    return {
      t: () => t,
      tabla: (c) => (t[c] ??= []),
      guardar: () => { try { localStorage.setItem(LLAVE, JSON.stringify(t)); } catch { /* sin almacenamiento: queda en memoria */ } },
      reiniciar: async () => { try { localStorage.removeItem(LLAVE); } catch { /* nada */ } t = await original(); },
      nuevoId: () => `demo${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
    };
  })());
}

export async function proveedorNavegador(urlDatos: string, rolActual: () => Rol = () => "Dirección"): Promise<Proveedor> {
  const a = await almacenNavegador(urlDatos);
  await asegurarDemoReservas(a);
  const t = new Proxy({} as Tablas, { get: (_o, k: string) => a.t()[k], set: (_o, k: string, v) => { a.t()[k] = v; return true; }, ownKeys: () => Reflect.ownKeys(a.t()), getOwnPropertyDescriptor: (_o, k) => Object.getOwnPropertyDescriptor(a.t(), k) });
  const guardar = a.guardar;
  const tabla = a.tabla;
  const buscar = (c: string, id: string) => tabla(c).find((f) => f.id === id);
  const nuevoId = a.nuevoId;
  const ahora = () => new Date().toISOString();
  const bit = (tablaN: string, registroId: string, accion: string, antes?: unknown, despues?: unknown, motivo?: string) =>
    tabla("bitacora").unshift({ id: nuevoId(), creadoEn: ahora(), tabla: tablaN, registroId, accion, motivo: motivo ?? null, antes: antes ?? null, despues: despues ?? null });
  const unico = (c: string, campo: string, valor: unknown, id?: string) => {
    if (valor === null || valor === undefined || valor === "") return;
    if (tabla(c).some((f) => f[campo] === valor && f.id !== id)) throw new Error("Ya existe un registro con ese valor (debe ser único).");
  };
  const unicos: Record<string, string[]> = { usuarios: ["usuario"], roles: ["nombre"], tours: ["slug"], cabanas: ["slug"], cupones: ["codigo"], recursos: ["numero"], agencias: ["usuarioPortal"], temporadas: ["clave"], tiposUsuario: ["nombre"], proveedoresReserva: ["usuario"] };

  return {
    modo: "navegador",
    async listar(c) {
      const rol = rolActual();
      const r = moduloPara(c, rol);
      let filas = tabla(c).map((f) => (c === "agencias" || c === "proveedoresReserva" ? sinSecretos(f) : f));
      if (r.propias?.roles.includes(rol)) { const yo = quienEsLocal(a, rol)?.id ?? "sin-usuario"; filas = filas.filter((f) => f[r.propias!.campo] === yo); }
      if (r.orden) { const { campo, dir } = r.orden; filas.sort((a, b) => String(a[campo] ?? "").localeCompare(String(b[campo] ?? "")) * (dir === "asc" ? 1 : -1)); }
      return filas.map((f) => visibleDe(r, rol, f));
    },
    async crear(c, entrada) {
      const rol = rolActual();
      const r = moduloPara(c, rol);
      if (!r.crear) throw new Error("Este módulo no permite crear registros aquí");
      const { datos, errores } = aBase(r, entradaPermitida(r, rol, entrada), false);
      if (errores.length) throw new Error(errores.join(". "));
      const defectos = Object.fromEntries(r.campos.filter((x) => x.porDefecto !== undefined && !(x.nombre in datos)).map((x) => [x.nombre, x.porDefecto === "ahora" ? ahora() : x.porDefecto]));
      if (c === "agencias") await altaAgenciaLocal(a, datos);
      if (c === "proveedoresReserva") await altaProveedorLocal(a, datos);
      if (c === "soporte") prepararTicketLocal(a, rol, datos);
      if (r.propias?.roles.includes(rol)) datos[r.propias.campo] = quienEsLocal(a, rol)?.id ?? null;
      const fila = serializar({ id: nuevoId(), ...defectos, ...datos, ...(r.campos.some((x) => x.nombre === "creadoEn") ? { creadoEn: ahora() } : {}) }) as Fila;
      for (const u of unicos[c] ?? []) unico(c, u, fila[u]);
      tabla(c).unshift(fila);
      bit(r.modelo, fila.id, "crear", undefined, sinSecretos(fila));
      guardar();
      return visibleDe(r, rol, sinSecretos(fila));
    },
    async editar(c, id, entrada) {
      const rol = rolActual();
      const r = moduloPara(c, rol);
      if (!r.editar) throw new Error("Este módulo no se edita directamente");
      const fila = buscar(c, id);
      // Quien solo ve lo suyo no puede tocar lo de otros: para él no existe (como en el servidor).
      if (!fila || (r.propias?.roles.includes(rol) && fila[r.propias.campo] !== (quienEsLocal(a, rol)?.id ?? "sin-usuario"))) throw new Error("No existe");
      const { datos, errores } = aBase(r, entradaPermitida(r, rol, entrada), true);
      if (errores.length) throw new Error(errores.join(". "));
      const nuevos = serializar(datos);
      for (const u of unicos[c] ?? []) if (u in nuevos) unico(c, u, nuevos[u], id);
      const antes = { ...fila };
      Object.assign(fila, nuevos);
      bit(r.modelo, id, "editar", sinSecretos(antes as Fila), sinSecretos(fila));
      guardar();
      return visibleDe(r, rol, sinSecretos(fila));
    },
    async borrar(c, id) {
      const r = moduloPara(c, rolActual());
      if (!r.borrar) throw new Error("Este registro no se borra. Desactívalo o cancélalo.");
      const ligados = Object.entries(t).some(([k, filas]) => recursoPor(k)?.campos.some((x) => x.ref === c && filas.some((f) => f[x.nombre] === id)));
      if (ligados) throw new Error("Tiene registros ligados; no se puede borrar. Desactívalo.");
      t[c] = tabla(c).filter((f) => f.id !== id);
      bit(r.modelo, id, "borrar");
      guardar();
    },
    async accion(c, id, nombre, d) {
      const rol = rolActual();
      const def = moduloPara(c, rol).acciones?.find((x) => x.id === nombre);
      if (def?.roles && !def.roles.includes(rol)) throw new Error("Tu rol no puede hacer esto");
      const res = (await accionLocal(a, rolActual(), c, id, nombre, d)) ?? ejecutar(c, id, nombre, d);
      guardar();
      return res;
    },
    async reiniciar() { await a.reiniciar(); },
  };

  function ejecutar(c: string, id: string, nombre: string, d: Record<string, string>) {
    if (c === "ventas" && nombre === "confirmarPago") {
      const v = buscar("ventas", id);
      if (!v || v.estado !== "apartada") throw new Error("Solo se confirma una venta apartada");
      tabla("pagos").unshift({ id: nuevoId(), ventaId: id, metodo: d.metodo || "efectivo", montoCentavos: v.totalCentavos, estado: "aprobado", referencia: "PANEL", creadoEn: ahora() });
      v.estado = "pagada";
      bit("venta", id, "confirmar pago", { estado: "apartada" }, { estado: "pagada", metodo: d.metodo });
      return { mensaje: "Pago confirmado" };
    }
    if (c === "ventas" && nombre === "cancelar") {
      if (!d.motivo?.trim()) throw new Error("Escribe el motivo");
      const v = buscar("ventas", id);
      if (!v || (v.estado !== "apartada" && v.estado !== "pagada")) throw new Error("Esta venta ya no se puede cancelar");
      for (const b of tabla("boletos").filter((b) => b.ventaId === id && b.estado !== "cancelado")) {
        b.estado = "cancelado";
        const a = buscar("asignaciones", String(b.asignacionId));
        if (a) a.ocupados = Number(a.ocupados) - Number(b.adultos) - Number(b.ninos ?? 0);
      }
      if (v.estado === "pagada") tabla("pagos").unshift({ id: nuevoId(), ventaId: id, metodo: "efectivo", montoCentavos: -Number(v.totalCentavos), estado: "devuelto", referencia: "CANCELACION", creadoEn: ahora() });
      const antes = v.estado;
      v.estado = "cancelada";
      bit("venta", id, "cancelar", { estado: antes }, { estado: "cancelada" }, d.motivo);
      return { mensaje: "Venta cancelada; lugares liberados" };
    }
    if (c === "salidas" && nombre === "cambiarHorario") {
      if (!/^\d{2}:\d{2}$/.test(d.hora ?? "")) throw new Error("Escribe la hora como 10:30");
      const s = buscar("salidas", id);
      if (!s) throw new Error("No existe");
      if (tabla("salidas").some((x) => x.id !== id && x.tourId === s.tourId && x.fecha === s.fecha && x.hora === d.hora)) throw new Error("Ya hay una salida de ese tour a esa hora. Agrega un camión a esa salida.");
      const antes = s.hora;
      s.hora = d.hora;
      const asig = new Set(tabla("asignaciones").filter((a) => a.salidaId === id).map((a) => a.id));
      let avisos = 0;
      for (const b of tabla("boletos").filter((b) => asig.has(String(b.asignacionId)) && b.estado !== "cancelado")) {
        const v = buscar("ventas", String(b.ventaId));
        const cl = v && buscar("clientes", String(v.clienteId));
        tabla("envios").unshift({ id: nuevoId(), boletoId: b.id, canal: "whatsapp", destino: cl?.telefono ?? "", estado: "pendiente: cambio de horario", creadoEn: ahora() });
        avisos++;
      }
      bit("salida", id, "cambiar horario", { hora: antes }, { hora: d.hora });
      return { mensaje: `Horario cambiado a las ${d.hora}. ${avisos} boletos se movieron y quedan avisos por enviar.` };
    }
    if (c === "boletos" && nombre === "abordar") {
      if (!d.motivo?.trim()) throw new Error("El abordaje manual necesita motivo");
      const b = buscar("boletos", id);
      const v = b && buscar("ventas", String(b.ventaId));
      if (!b || b.estado !== "vigente" || v?.estado !== "pagada") throw new Error("Solo aborda un boleto vigente y pagado");
      tabla("abordajes").unshift({ id: nuevoId(), boletoId: id, fechaHora: ahora(), metodo: "manual", lugares: Number(b.adultos) + Number(b.ninos ?? 0), registroId: tabla("usuarios")[0]?.id ?? null, motivo: d.motivo });
      b.estado = "abordado";
      bit("boleto", id, "abordaje manual", undefined, undefined, d.motivo);
      return { mensaje: "Abordaje registrado" };
    }
    if (c === "boletos" && nombre === "reenviar") {
      const b = buscar("boletos", id);
      const v = b && buscar("ventas", String(b.ventaId));
      const cl = v && buscar("clientes", String(v.clienteId));
      if (!b || !cl) throw new Error("No existe");
      const canal = d.canal === "correo" ? "correo" : "whatsapp";
      const destino = canal === "correo" ? cl.correo : cl.telefono;
      if (!destino) throw new Error("El cliente no tiene correo");
      tabla("envios").unshift({ id: nuevoId(), boletoId: id, canal, destino, estado: "enviado", creadoEn: ahora() });
      return { mensaje: `Boleto reenviado por ${canal}` };
    }
    throw new Error("Acción no disponible");
  }
}
